import {
  Box,
  Button,
  Container,
  Grid,
  GridItem,
  Heading,
  Input,
  Spinner,
  Text,
  VStack
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { mockApi, type CreateCampaignData } from "../api/mockApi";

function EditCampaign() {
  const { id } = useParams<{ id: string }>();
  const campaignId = id || "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    campaignName: "",
    adSetName: "",
    adName: "",
    platform: "",
    redirectUrl: ""
  });

  // Fetch campaign data
  const {
    data: campaign,
    isLoading: isLoadingData,
    error: fetchError
  } = useQuery({
    queryKey: ["campaign", campaignId],
    queryFn: () => mockApi.getCampaign(campaignId),
    enabled: !!campaignId
  });

  // Update campaign mutation
  const updateCampaignMutation = useMutation({
    mutationFn: (data: Partial<CreateCampaignData>) =>
      mockApi.updateCampaign(campaignId, data),
    onSuccess: (updatedCampaign) => {
      console.log("Campaign updated:", updatedCampaign);
      // Invalidate and refetch links list and the specific link
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["link", campaignId] });
      navigate("/campaigns");
    },
    onError: (error) => {
      console.error("Campaign update failed:", error);
    }
  });

  // Update form data when link is loaded
  useEffect(() => {
    if (campaign) {
      setFormData({
        campaignName: campaign.campaign_name || "",
        adSetName: campaign.adset_name || "",
        adName: campaign.ad_name || "",
        platform: "",
        redirectUrl: ""
      });
    }
  }, [campaign]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const campaignData: Partial<CreateCampaignData> = {
      campaignName: formData.campaignName,
      adSetName: formData.adSetName,
      adName: formData.adName,
      platform: formData.platform,
      redirectUrl: formData.redirectUrl
    };

    updateCampaignMutation.mutate(campaignData);
  };

  // Platform options - USA/CA relevant platforms
  const platformOptions = [
    { value: "", label: "Select Platform" },
    { value: "Google Ads", label: "Google Ads" },
    { value: "YouTube Ads", label: "YouTube Ads" },
    { value: "Facebook Ads", label: "Facebook Ads" },
    { value: "Instagram Ads", label: "Instagram Ads" },
    { value: "TikTok Ads", label: "TikTok Ads" },
    { value: "Snapchat Ads", label: "Snapchat Ads" },
    { value: "LinkedIn Ads", label: "LinkedIn Ads" },
    { value: "X Ads (Twitter)", label: "X Ads (Twitter)" },
    { value: "Pinterest Ads", label: "Pinterest Ads" },
    { value: "Amazon Ads", label: "Amazon Ads" },
    { value: "Microsoft Ads", label: "Microsoft Ads (Bing)" },
    { value: "Reddit Ads", label: "Reddit Ads" },
    { value: "Custom", label: "Custom" },
    { value: "Other", label: "Other" }
  ];

  if (isLoadingData) {
    return (
      <Container maxW="4xl" py={8}>
        <Box textAlign="center">
          <Spinner size="lg" color="blue.500" />
          <Text fontSize="lg" color="gray.600" mt={4}>
            Loading campaign data...
          </Text>
        </Box>
      </Container>
    );
  }

  if (fetchError) {
    return (
      <Container maxW="4xl" py={8}>
        <Box textAlign="center">
          <Box
            p={4}
            bg="red.50"
            border="1px"
            borderColor="red.200"
            rounded="md"
            mb={4}
          >
            <Text color="red.600" fontWeight="medium">
              Error loading campaign
            </Text>
            <Text color="red.500" fontSize="sm">
              {fetchError.message}
            </Text>
          </Box>
          <Link to="/campaigns">
            <Text
              color="blue.500"
              fontWeight="medium"
              textDecoration="underline"
            >
              ← Back to Campaigns
            </Text>
          </Link>
        </Box>
      </Container>
    );
  }

  if (!campaign) {
    return (
      <Container maxW="4xl" py={8}>
        <Box textAlign="center">
          <Text fontSize="lg" color="red.600" mb={4}>
            Campaign not found
          </Text>
          <Link to="/campaigns">
            <Text
              color="blue.500"
              fontWeight="medium"
              textDecoration="underline"
            >
              ← Back to Campaigns
            </Text>
          </Link>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxW="4xl" py={8}>
      <Box mb={6}>
        <Link to="/campaigns">
          <Text color="blue.500" fontWeight="medium" textDecoration="underline">
            ← Back to Campaigns
          </Text>
        </Link>
      </Box>

      <Heading size="xl" mb={8} color="gray.800">
        Edit Campaign: {campaign.campaign_id || "Unknown ID"}
      </Heading>

      <Box bg="white" rounded="lg" shadow="md" p={8}>
        {/* Campaign Info */}
        <Box mb={6} p={4} bg="gray.50" rounded="md">
          <Text fontSize="sm" color="gray.600" mb={2}>
            Campaign Information
          </Text>
          <VStack align="start" gap={1}>
            <Text fontSize="sm">
              <strong>ID:</strong> {campaign.campaign_id || "N/A"}
            </Text>
            <Text fontSize="sm">
              <strong>Created:</strong>{" "}
              {campaign.created_time
                ? new Date(campaign.created_time).toLocaleString()
                : "N/A"}
            </Text>
            <Text fontSize="sm">
              <strong>Date Range:</strong>{" "}
              {campaign.date_start
                ? `${new Date(campaign.date_start).toLocaleDateString()} - ${campaign.date_stop ? new Date(campaign.date_stop).toLocaleDateString() : "Ongoing"}`
                : "N/A"}
            </Text>
          </VStack>
        </Box>

        <Box as="form" onSubmit={handleSubmit}>
          <VStack gap={6} align="stretch">
            <Box>
              <Text mb={2} fontWeight="medium">
                Platform *
              </Text>
              <Box>
                <select
                  name="platform"
                  value={formData.platform}
                  onChange={handleChange}
                  disabled={updateCampaignMutation.isPending}
                  required
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    border: "1px solid #E2E8F0",
                    borderRadius: "0.375rem",
                    fontSize: "1rem",
                    backgroundColor: "white"
                  }}
                >
                  {platformOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Box>
              <Text fontSize="sm" color="gray.500" mt={1}>
                Choose the advertising platform for this campaign
              </Text>
            </Box>

            <Box>
              <Text mb={2} fontWeight="medium">
                Campaign Name (Optional)
              </Text>
              <Input
                name="campaignName"
                value={formData.campaignName}
                onChange={handleChange}
                placeholder="Enter campaign name"
                disabled={updateCampaignMutation.isPending}
              />
              <Text fontSize="sm" color="gray.500" mt={1}>
                A descriptive name for your advertising campaign
              </Text>
            </Box>

            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <GridItem>
                <Text mb={2} fontWeight="medium">
                  Ad Set Name (Optional)
                </Text>
                <Input
                  name="adSetName"
                  value={formData.adSetName}
                  onChange={handleChange}
                  placeholder="Enter ad set name"
                  disabled={updateCampaignMutation.isPending}
                />
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Target audience group name
                </Text>
              </GridItem>

              <GridItem>
                <Text mb={2} fontWeight="medium">
                  Ad Name (Optional)
                </Text>
                <Input
                  name="adName"
                  value={formData.adName}
                  onChange={handleChange}
                  placeholder="Enter ad name"
                  disabled={updateCampaignMutation.isPending}
                />
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Individual ad creative name
                </Text>
              </GridItem>
            </Grid>

            <Box>
              <Text mb={2} fontWeight="medium">
                Redirect URL *
              </Text>
              <Input
                name="redirectUrl"
                type="url"
                value={formData.redirectUrl}
                onChange={handleChange}
                placeholder="https://example.com/landing-page"
                disabled={updateCampaignMutation.isPending}
                required
              />
              <Text fontSize="sm" color="gray.500" mt={1}>
                The URL where users will be redirected when they click on your
                ad
              </Text>
            </Box>

            {updateCampaignMutation.error && (
              <Box p={3} bg="red.50" color="red.600" rounded="md">
                {updateCampaignMutation.error.message}
              </Box>
            )}

            <Box display="flex" gap={4} justifyContent="flex-end" pt={4}>
              <Link to="/campaigns">
                <Button
                  variant="outline"
                  disabled={updateCampaignMutation.isPending}
                >
                  Cancel
                </Button>
              </Link>
              <Button
                type="submit"
                colorPalette="blue"
                loading={updateCampaignMutation.isPending}
              >
                Update Campaign
              </Button>
            </Box>
          </VStack>
        </Box>
      </Box>
    </Container>
  );
}

export default EditCampaign;
